import { Eye, EyeOff } from "lucide-react";
import { useState, useEffect } from "react";



import { resetPassword } from "@/api/authentication";
import { logout } from "@/auth";
import { changeSessionPassword } from "@/auth/change-password";
import { Link } from "react-router-dom";



import PasswordResetSuccess from "@/components/widget/password-reset-success";



import { AuthInput } from "@/components/ui/auth-input";
import { Button } from "@/components/ui/button";


const BackToDashboardLink = () => {
  const handleClick = () => {
    logout();
  };

  return (
    <Link
      to="/dashboard/login"
      onClick={handleClick}
      className="flex items-center gap-1 text-sm transition-opacity hover:opacity-80"
      style={{
        color: "#0068B8",
        fontSize: "14px"
      }}
    >
      <span>→</span>
      <span>بازگشت</span>
    </Link>
  );
};

const STYLES = {
  heading: {
    fontWeight: 400,
    fontSize: "20px",
    lineHeight: "20px",
    color: "#002248"
  },
  description: {
    fontWeight: 400,
    fontSize: "15px",
    lineHeight: "15px",
    color: "#000000"
  },
  button: {
    width: "108px",
    height: "32px",
    padding: "7px 0",
    background: "#057C11",
    boxShadow: "0px 10px 10px rgba(0, 0, 0, 0.25)",
    fontWeight: 400,
    fontSize: "15px",
    lineHeight: "15px",
    color: "#FFF8F8",
    borderRadius: "4px"
  },
  errorText: {
    fontSize: "12px",
    color: "#DC2626"
  }
} as const;

interface ChangePasswordProps {
  requireOldPassword?: boolean;
  token?: string;
}

const ChangePassword = ({
  requireOldPassword = true,
  token
}: ChangePasswordProps) => {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [repeatNewPassword, setRepeatNewPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorKey, setErrorKey] = useState(0); // برای ریست کردن انیمیشن
  const [isShaking, setIsShaking] = useState(false);
  const [showSuccessPage, setShowSuccessPage] = useState(false);

  // رمز: حداقل ۸ کاراکتر، یک حرف بزرگ، یک عدد
  const passwordRegex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d).{8,}$/;

  // انیمیشن لرزش برای خطا
  useEffect(() => {
    if (error) {
      setIsShaking(true);
      const timer = setTimeout(() => setIsShaking(false), 650);
      return () => clearTimeout(timer);
    }
  }, [error, errorKey]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (repeatNewPassword !== newPassword) {
      setError("تکرار رمز عبور با رمز عبور جدید مطابقت ندارد.");
      setErrorKey((prev) => prev + 1);
      return;
    }

    if (!passwordRegex.test(newPassword)) {
      setError(
        "رمز عبور باید حداقل ۸ کاراکتر، شامل یک حرف بزرگ، یک حرف کوچک و یک عدد باشد."
      );
      setErrorKey((prev) => prev + 1);
      return;
    }

    if (requireOldPassword && !oldPassword.trim()) {
      setError("لطفاً رمز عبور فعلی خود را وارد کنید.");
      setErrorKey((prev) => prev + 1);
      return;
    }

    setIsSubmitting(true);

    try {
      // اگر توکن داریم، از API resetPassword استفاده می‌کنیم
      if (token) {
        const result = await resetPassword({
          token,
          newPassword,
          confirmPassword: repeatNewPassword
        });

        if (result && result.status) {
          // موفقیت - نمایش صفحه موفقیت
          setError(null);
          setShowSuccessPage(true);
        } else {
          setError(
            result?.message || "خطا در تغییر رمز عبور. لطفاً دوباره تلاش کنید."
          );
          setErrorKey((prev) => prev + 1);
        }
      } else {
        // اگر توکن نداریم، از API changeSessionPassword استفاده می‌کنیم
        const formData = new FormData(e.currentTarget);
        const data: Record<string, any> = {};
        formData.forEach((value, key) => {
          data[key] = value;
        });

        await changeSessionPassword(data as any);
        // موفقیت - نمایش صفحه موفقیت
        setShowSuccessPage(true);
      }
    } catch {
      setError("خطا در تغییر رمز عبور. لطفاً دوباره تلاش کنید.");
      setErrorKey((prev) => prev + 1);
    } finally {
      setIsSubmitting(false);
    }
  };

  // اگر رمز عبور با موفقیت تغییر کرد، صفحه موفقیت را نمایش بده
  if (showSuccessPage) {
    return <PasswordResetSuccess />;
  }

  return (
    <div className="w-full">
      {/* Title Section - Group 814 */}
      <div className="mb-6 space-y-4">
        <h2
          className="flex items-center justify-center text-center"
          style={STYLES.heading}
        >
          بازیابی رمز عبور جدید
        </h2>
        <p
          className="flex items-center justify-center text-center"
          style={STYLES.description}
        >
          لطفاً رمز عبور جدید خود را وارد نموده و تایید نمایید.
        </p>
      </div>

      {/* Form - Group 690 */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Old Password Field - if required */}
        {requireOldPassword && (
          <div className="relative">
            <AuthInput
              label="رمز عبور فعلی"
              name="oldPassword"
              type={showOldPassword ? "text" : "password"}
              labelWidth="82px"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowOldPassword(!showOldPassword)}
              className="absolute bottom-0 left-0 h-[20px]"
              tabIndex={-1}
            >
              {showOldPassword ? (
                <EyeOff size={16} className="text-gray-500" />
              ) : (
                <Eye size={16} className="text-gray-500" />
              )}
            </button>
          </div>
        )}

        {/* New Password Field - Group 686 */}
        <div className="relative">
          <AuthInput
            label="رمز عبور جدید"
            name="newPassword"
            type={showNewPassword ? "text" : "password"}
            labelWidth="82px"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
          />
          <button
            type="button"
            onClick={() => setShowNewPassword(!showNewPassword)}
            className="absolute bottom-0 left-0 h-[20px]"
            tabIndex={-1}
          >
            {showNewPassword ? (
              <EyeOff size={16} className="text-gray-500" />
            ) : (
              <Eye size={16} className="text-gray-500" />
            )}
          </button>
        </div>

        {/* Repeat Password Field */}
        <div className="relative">
          <AuthInput
            label="تکرار رمز عبور"
            name="repeat-new-password"
            type={showRepeatPassword ? "text" : "password"}
            labelWidth="82px"
            value={repeatNewPassword}
            onChange={(e) => setRepeatNewPassword(e.target.value)}
            autoComplete="new-password"
          />
          <button
            type="button"
            onClick={() => setShowRepeatPassword(!showRepeatPassword)}
            className="absolute bottom-0 left-0 h-[20px]"
            tabIndex={-1}
          >
            {showRepeatPassword ? (
              <EyeOff size={16} className="text-gray-500" />
            ) : (
              <Eye size={16} className="text-gray-500" />
            )}
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <p
            className={`text-center ${isShaking ? "animate-shake" : ""}`}
            style={STYLES.errorText}
          >
            {error}
          </p>
        )}

        {/* Submit Button - Frame 4 */}
        <div className="flex items-center justify-between pt-2">
          <BackToDashboardLink />

          <Button
            type="submit"
            disabled={
              newPassword !== repeatNewPassword ||
              !newPassword ||
              !repeatNewPassword ||
              isSubmitting ||
              (requireOldPassword && !oldPassword.trim())
            }
            className="flex items-center justify-center hover:opacity-90 disabled:opacity-50"
            style={STYLES.button}
          >
            {isSubmitting ? "در حال ارسال..." : "تایید"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ChangePassword;